import {IUsuario} from "@models/conta/IUsuario.ts";
import type {IFormaPagamento, IFormaPagamentoFormEntry} from "@models/formaPagamento/IFormaPagamento.ts";
import {IFornecedor} from "@models/fornecedores/fornecedor";
import {ISolicitacao, type ISolicitacaoItem} from "./solicitacoes";
import {Dayjs} from 'dayjs';
import {IRecursoTransferenciaSolicitacao} from "@models/recurso/IRecursoTransferencia.ts";
import {IArquivoOrcamento} from "@models/sistema/IArquivo.ts";
import {ISolicitacaoOrcamentoFlag} from "@models/orcamento/ISolicitacaoOrcamentoFlag.ts";
import {ISolicitacaoOrcamentoRastreio} from "@models/orcamento/ISolicitacaoOrcamentoRastreio.ts";

export interface ISolicitacaoOrcamento {
    id: number;
    descricao?: string;
    fornecedorId: number;
    valor: number;
    qtdAtendida: number;
    usuarioOrcamentoId: number;
    solicitacaoId: number;
    solicitacaoItemId?: number;
    dataOrcamento: Dayjs;
    dataValidade?: Dayjs;
    usuarioConfirmacaoId?: number;
    dataConfirmacaoSolicitante?: Dayjs;
    usuarioPagamentoId?: number;
    dataPagamentoEfetuado?: Dayjs;
    usuarioAutorizadorId?: number;
    dataCompraAutorizada?: Dayjs;
    usuarioCompradorId?: number;
    usuarioFinalizacaoId?: number;
    dataCompraEfetuada?: Dayjs;
    usuarioConfirmaEntregaId?: number;
    dataEntrega?: Dayjs;
    dataFinalizacao?: Dayjs;
    status: StatusAprovacaoOrcamento;
    formaPagamentoId: number;
    fornecedor?: IFornecedor;
    solicitacao?: ISolicitacao;
    solicitacaoItem?: ISolicitacaoItem;
    aprovacoes: IAprovacaoOrcamento[];
    arquivos: IArquivoOrcamento[];
    formaPagamento?: IFormaPagamento;
    transferencia?: IRecursoTransferenciaSolicitacao;
    pagamentoFormEntries: IFormaPagamentoFormEntry[];
    usuarioOrcamento?: IUsuario;
    usuarioConfirmacao?: IUsuario;
    usuarioPagamento?: IUsuario;
    usuarioAutorizador?: IUsuario;
    usuarioComprador?: IUsuario;
    usuarioConfirmaEntrega?: IUsuario;
    usuarioFinalizacao?: IUsuario
    flags?: ISolicitacaoOrcamentoFlag[]
    rastreios?: ISolicitacaoOrcamentoRastreio[]

    // Propriedade Computada
    readonly valorTotal: number;
}

export type StatusAprovacaoType = 'Pendente' | 'Aprovado' | 'Rejeitado' | 'Cancelado' | 'Finalizado'
export const statusAprovacaoColor = (status: StatusAprovacaoType) => {
    return {
        'Pendente': 'warning',
        'Aprovado': 'success',
        'Rejeitado': 'error',
        'Cancelado': 'error',
        'Finalizado': 'info',
    }[status] ?? 'inherit';
};


export type StatusAprovacaoOrcamento = 'PendenteSolicitante'
    | 'PendenteAprovador'
    | 'PendenteRecurso'
    | 'PendenteComprador'
    | 'PendentePagamento'
    | 'AguardandoEntrega'
    | 'Pendente'
    | 'Finalizado'
    | 'Rejeitado'
    | 'Cancelado'

export const statusAprovacaoOrcamentoValidators = {
    disabled: ['Finalizado', 'Rejeitado', 'Cancelado'] as StatusAprovacaoOrcamento[],
    emAprovacao: ['PendenteSolicitante', 'PendenteAprovador', 'PendenteRecurso'] as StatusAprovacaoOrcamento[],
    precisaDinheiro: ['PendenteAprovador', 'PendenteRecurso'] as StatusAprovacaoOrcamento[],
} as const;


export const StatusAprovacaoOrcamentoValues = [
    'PendenteSolicitante',
    'PendenteAprovador',
    'PendenteRecurso',
    'PendenteComprador',
    'PendentePagamento',
    'Finalizado',
    'Rejeitado',
    'Cancelado',
] as StatusAprovacaoOrcamento[];

export const PermStatusAprovacaoOrcamento = {
    PermiteAlterarDadosPagamento: ['PendenteSolicitante',
        'PendenteAprovador',
        'PendenteRecurso',
        'PendenteComprador',
        'PendentePagamento'] as StatusAprovacaoOrcamento[],
};


export interface IAprovacao {
    id: number;
    aprovadorId: number;
    aprovador?: IUsuario;
    solicitacaoId: number;
    solicitacao?: ISolicitacao;
    dataSolicitacao: Dayjs;
    status: StatusAprovacaoType;
    dataAprovacao?: Dayjs;
    dataLimite: Dayjs;
    motivo: string;
    tipoAprovacao: string;
}

export interface IAprovacaoOrcamento extends IAprovacao {
    orcamentoId?: number;
    orcamento?: ISolicitacaoOrcamento;
}